<template>
  <div :class="themeClass" class="multiselect-block">
    <div :id="wrapperId" class="multiselect-wrapper" :class="multiselectClass">
      <label class="label !flex items-center gap-2" :class="labelClasses" :for="id">
        <span class="flex items-center">
          {{ label }}
          <slot name="after-label" />
        </span>
        <OurSvgIcon
          v-if="reload"
          color="gray"
          :src="icons.Reload"
          class="icon"
          :class="{ 'animate-spin': loading }"
          interactive
          :data-cy="`${dataCy}-caret`"
          @click.prevent="reloadData"
        />
      </label>

      <Multiselect
        :id="id"
        ref="multiselect"
        v-model="selectValue"
        class="multiselect"
        :options="withInternalSearch ? options : asyncOptions"
        :options-limit="optionsLimit"
        :multiple="true"
        :label="itemLabel"
        :track-by="valueKey"
        :open-direction="openDirection"
        select-label=""
        deselect-label=""
        selected-label=""
        :hide-selected="isHideSelected"
        :group-label="groupLabel"
        :group-values="groupValues"
        :disabled="disabled"
        :placeholder="placeholderState"
        :allow-empty="allowEmpty"
        :internal-search="withInternalSearch"
        :loading="isLoading"
        :taggable="taggable"
        :tag-position="tagPosition"
        :data-cy="dataCy"
        tag-placeholder=""
        :searchable="searchable"
        :option-height="optionHeight"
        @open="onOpen"
        @close="onClose"
        @search-change="onSearchChange"
      >
        <template #selection="{ values }">
          <span
            v-if="defaultTags && values.length"
            class="selected-count"
            :data-cy="`${dataCy}-selected-count`"
          >
            {{ selectedCountLabel }}
          </span>

          <div v-else-if="!defaultTags" class="tags">
            <div v-for="(tag, index) in values" :key="index" class="tags-wrapper">
              <div
                v-if="tag"
                class="tags-title"
                :data-cy="`${dataCy}-item-${index}`"
                :class="hideInactiveElement(tag)"
              >
                <!-- @slot Use it to add tag title. -->
                <slot name="tag-title" :tag="tag">
                  {{ tag[itemLabel] }}
                </slot>
              </div>

              <OurSvgIcon
                v-if="!disabled"
                size="sm"
                :src="icons.Close"
                interactive
                color="gray"
                :data-cy="`${dataCy}-close`"
                @click="onClickRemoveValue(index)"
              />
            </div>
          </div>
        </template>

        <template #option="props">
          <!-- @slot Use it to add option. -->
          <slot :scope-props="props" name="option" />

          <div
            v-if="!isShownOptionSlot"
            :style="hierarchicalOptionStyle(props.option.level)"
            class="select-label hierarchical-option"
          >
            <span
              v-if="props.option.level > 1"
              class="hierarchical-option-dot"
              :style="hierarchicalDotStyle(props.option.level)"
            />
            <span>{{ getOptionDisplayLabel(props.option) }}</span>
          </div>
        </template>

        <template #noResult>
          {{ i18n.listIsEmpty }}
        </template>

        <template #noOptions>
          {{ i18n.noDataToShow }}
        </template>

        <template #afterList>
          <li v-if="isSelectedAllOptions" class="multiselect__element">
            <span class="multiselect__option">
              {{ i18n.listIsEmpty }}
            </span>
          </li>

          <div v-else-if="addOption" class="multiselect-add-wrapper" @click="onClickAddOption">
            <div class="multiselect-add-title">
              {{ i18n.add }}
              <span class="multiselect-add-title-command">{{ addOptionKeyCombination }}</span>
            </div>

            <OurButton ref="addOptionButton" class="multiselect-add-button">
              <OurSvgIcon
                :src="icons.Plus2"
                class="multiselect-add-icon"
                color="white"
                pill
                size="2xs"
              />
            </OurButton>
          </div>
        </template>

        <template #caret="{ toggle }">
          <div
            v-show="!selectValue.length"
            class="multiselect-angle"
            @mousedown.prevent.stop="toggle"
          >
            <OurSvgIcon
              color="gray"
              :src="icons.AngleDown"
              class="icon"
              interactive
              :data-cy="`${dataCy}-caret`"
            />
          </div>

          <div v-if="selectValue.length" class="relative">
            <div class="multiselect-clear" @click="onClickClearAll">
              <div class="multiselect-clear-text">
                {{ i18n.clear }}
              </div>
            </div>
          </div>
        </template>
      </Multiselect>

      <div v-if="isShownAfterCaretSlot" class="after-caret">
        <!-- @slot Use it to add something after caret. -->
        <slot name="after-caret" />
      </div>
    </div>

    <template v-if="description">
      <div class="description">
        {{ description }}
      </div>
    </template>

    <p v-if="error" class="error-message" :data-cy="`${dataCy}-error-message`">
      {{ error }}
    </p>
  </div>
</template>

<script>
import { getThemeClass, getRandomId, isMac, PX_IN_REM } from "@/services/_bit/UiService";
import I18nServiceDefault from "@/services/_bit/I18nService";
import debounce from "lodash.debounce";

import Multiselect from "vue-multiselect";
import OurButton from "@/components/_bit/Button";
import OurSvgIcon from "@/components/_bit/IconSvg";

const OPTIONS_LIMIT = 1000;

export default {
  name: "OurMultiselect",

  components: {
    Multiselect,
    OurButton,
    OurSvgIcon,
  },

  props: {
    /**
     * Generates unique element id.
     * @ignore
     */
    id: {
      type: String,
      default: () => getRandomId(),
    },

    /**
     * Set component select value.
     */
    value: {
      type: Array,
      default: () => [],
    },

    /**
     * Set options for component.
     */
    options: {
      type: Array,
      default: () => [],
    },

    /**
     * Set component label.
     */
    label: {
      type: String,
      default: "",
    },

    /**
     * Label from option Object, that will be visible in the dropdown.
     */
    itemLabel: {
      type: String,
      default: "label",
    },

    /**
     * Set input placeholder.
     */
    placeholder: {
      type: String,
      default: "",
    },

    /**
     * Set error message.
     */
    error: {
      type: String,
      default: "",
    },

    /**
     * Make component inactive.
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    reload: {
      type: Boolean,
      default: false,
    },

    loading: {
      type: Boolean,
      default: false,
    },

    /**
     * Allows to remove all selected values. Otherwise one must be left selected.
     */
    allowEmpty: {
      type: Boolean,
      default: true,
    },

    /**
     * Allow internal search.
     */
    withInternalSearch: {
      type: Boolean,
      default: true,
    },

    /**
     * Set limits the options displayed in the dropdown to the first X options.
     */
    optionsLimit: {
      type: Number,
      default: OPTIONS_LIMIT,
    },

    /**
     * Set track value key.
     */
    valueKey: {
      type: String,
      default: "id",
    },

    /**
     * Fixed opening direction (instead of auto). Options are "above"/"top" or "below"/"bottom".
     * @values auto, top, bottom
     */
    openDirection: {
      type: String,
      default: "auto",
    },

    /**
     * Set description for component.
     */
    description: {
      type: String,
      default: "",
    },

    /**
     * By default new tags will appear above the search results. Changing to 'bottom' will revert this behaviour and will proritize the search results.
     * @values top, bottom
     */
    tagPosition: {
      type: String,
      default: "top",
    },

    /**
     * Allow return full value properties.
     */
    returnFullValue: {
      type: Boolean,
      default: false,
    },

    /**
     * Set name of the property containing the group label.
     */
    groupLabel: {
      type: String,
      default: "label",
    },

    /**
     * Set name of the property containing the group values.
     */
    groupValues: {
      type: String,
      default: "",
    },

    /**
     * Show add new option button to the list.
     */
    addOption: {
      type: Boolean,
      default: false,
    },

    /**
     * Allow search value in list.
     */
    searchable: {
      type: Boolean,
      default: true,
    },

    /**
     * Allow creating new tags from search input.
     */
    taggable: {
      type: Boolean,
      default: true,
    },

    /**
     * Sets data-cy attribute for correct element sampling in tests.
     */
    dataCy: {
      type: String,
      default: "",
    },

    hideSelected: {
      type: Boolean,
      default: true,
    },

    /**
     * Show selected count in the input and keep selected options visible in the dropdown.
     */
    defaultTags: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    isLoading: false,
    asyncValue: "",
    asyncOptions: [],
    isOpen: null,
    isFocused: false,
    optionHeight: 0,
  }),

  computed: {
    wrapperId() {
      return `wrapper${this.id}`;
    },

    icons: () => ({
      Close: require("@/components/_bit/IconSvg/icons/Close.svg"),
      Plus2: require("@/components/_bit/IconSvg/icons/Plus2.svg"),
      AngleDown: require("@/components/_bit/IconSvg/icons/Angle-down.svg"),
      Reload: require("@/components/_bit/IconSvg/icons/Update.svg"),
    }),

    selectValue: {
      get() {
        return this.value
          .map((item) => {
            if (this.groupValues) {
              const options = this.options.find((option) => {
                return option[this.groupValues].find((value) => {
                  return value[this.valueKey] === item;
                });
              });

              if (!options) return undefined;

              return options[this.groupValues].find((value) => {
                return value[this.valueKey] === item;
              });
            }

            const value = item?.[this.valueKey] || item;

            return this.options.find((option) => option[this.valueKey] === value);
          })
          .filter((item) => item !== undefined);
      },

      set(value) {
        let newValue = value;

        if (!this.returnFullValue) newValue = value.map((item) => item[this.valueKey]);

        this.$emit("input", newValue);
      },
    },

    themeClass() {
      return getThemeClass();
    },

    multiselectClass() {
      return {
        error: this.error,
        "multiselect-wrapper-disabled": this.disabled,
        "group-select": this.groupValues,
        "default-tags": this.defaultTags,
        "is-open": this.isOpen,
        "has-value": this.selectValue.length > 0,
      };
    },

    isHideSelected() {
      return this.defaultTags ? false : this.hideSelected;
    },

    selectedCountLabel() {
      return this.$t("selectedCount", { count: this.selectValue.length });
    },

    addOptionKeyCombination() {
      return isMac ? "(⌘ + Enter)" : "(Ctrl + Enter)";
    },

    getTranslation() {
      return new I18nServiceDefault(this, { componentName: this.$options.name }).getTranslation;
    },

    i18n() {
      return {
        clear: this.getTranslation("clear"),
        listIsEmpty: this.getTranslation("listIsEmpty"),
        noDataToShow: this.getTranslation("noDataToShow"),
        addMore: this.getTranslation("addMore"),
        add: this.getTranslation("add"),
      };
    },

    isSelectedAllOptions() {
      if (this.defaultTags) return false;

      return this.options.length && this.options.length === this.selectValue.length;
    },

    labelClasses() {
      return {
        "label-color": this.isOpen,
        "error-label": this.error,
      };
    },

    isShownAfterCaretSlot() {
      return !!this.$slots["after-caret"];
    },

    isShownOptionSlot() {
      return !!this.$slots.option;
    },

    placeholderState() {
      if (this.defaultTags && this.selectValue.length) return "";

      return this.selectValue.length
        ? this.i18n.addMore
        : this.placeholder || this.$t("placeholder.addMore");
    },

    multiselectContentWrapper() {
      return document.querySelector(`#${this.wrapperId} .multiselect__content-wrapper`);
    },
  },

  watch: {
    resetAsync() {
      this.asyncValue = "";
      this.asyncOptions = [];
    },

    isOpen: {
      handler: "onChangeOpenState",
      deep: true,
    },
  },

  created() {
    if (this.addOption) {
      document.addEventListener("keydown", this.onKeydownAddOption);
    }
  },

  mounted() {
    if (this.addOption) {
      this.multiselectContentWrapper.addEventListener("scroll", this.setAddOptionButtonPosition);
    }
  },

  beforeDestroy() {
    if (this.addOption) {
      document.removeEventListener("keydown", this.onKeydownAddOption);

      this.multiselectContentWrapper.removeEventListener("scroll", this.setAddOptionButtonPosition);
    }
  },

  methods: {
    setAddOptionButtonPosition() {
      const scrollBottomPosition = -this.multiselectContentWrapper.scrollTop;
      const defaultButtonPosition = 9;
      const addOptionPosition = (scrollBottomPosition + defaultButtonPosition) / PX_IN_REM;

      this.$refs.addOptionButton.$el.style.bottom = `${addOptionPosition}rem`;
    },

    reloadData() {
      this.$emit("reloadData");
    },

    onKeydownAddOption(event) {
      if (!this.isFocused) return;

      const isEnter = event.keyCode === 13;
      const isCtrl = event.ctrlKey;
      const isMeta = event.metaKey;

      if (isMeta && isEnter && isMac) {
        this.$emit("addOption");
      }

      if (isEnter && isCtrl && !isMac) {
        this.$emit("addOption");
      }
    },

    onClickAddOption() {
      this.$emit("addOption");
    },

    onOpen() {
      this.$emit("open");
      this.isOpen = true;
      this.isFocused = true;
    },

    onClose() {
      this.isOpen = false;

      setTimeout(() => {
        this.isFocused = false;
      }, 100);
    },

    onClickRemoveValue(index) {
      const intermediateValue = [...this.selectValue];

      intermediateValue.splice(index, 1);

      this.$emit(
        "input",
        intermediateValue.map((item) => item[this.valueKey])
      );
    },

    onClickClearAll() {
      this.selectValue = [];
    },

    onSearchChange: debounce(async function (query) {
      this.$emit("searchChange", query);
    }, 300),

    onChangeOpenState() {
      this.$nextTick(() => {
        this.optionHeight =
          this.$refs.multiselect.$refs.list.firstChild.firstElementChild.clientHeight;
      });
    },

    getOptionDisplayLabel(option) {
      if (!option) return "";

      if (option.isTag) return option.label || "";

      return option[this.itemLabel] ?? "";
    },

    hideInactiveElement(tag) {
      if (!tag || !tag.id) return "";

      const selectedId = this.options.find((option) => option.id === tag.id);

      if (!selectedId) return "";

      return selectedId.isHidden ? "cross-out-option" : "";
    },

    hierarchicalOptionStyle(level) {
      if (!level || level <= 1) return {};

      const baseMargin = 1;
      const fontSizeByLevel = {
        2: "0.875rem",
        3: "0.8125rem",
      };
      const fontSize = fontSizeByLevel[level] ?? "0.75rem";

      return {
        marginLeft: `${baseMargin * (level - 1)}rem`,
        fontSize,
      };
    },

    hierarchicalDotStyle(level) {
      const sizeByLevel = {
        2: "0.375rem",
        3: "0.3125rem",
      };
      const size = sizeByLevel[level] ?? "0.25rem";

      return {
        width: size,
        height: size,
      };
    },
  },
};
</script>

<i18n>
en:
  listIsEmpty: "List is empty."
  noDataToShow: "No data to show."
  clear: "clear"
  addMore: "Add more..."
  add: Add
  selectedCount: "{count} selected"
ua:
  listIsEmpty: "Список порожній."
  noDataToShow: "Дані відсутні."
  clear: "очистити"
  addMore: "Додати ще..."
  add: Додати
  selectedCount: "{count} обрано"
</i18n>

<style lang="postcss" scoped>
.multiselect {
  &-block {
    @apply relative;
  }

  &-wrapper {
    @apply flex w-full items-center;
    @apply rounded-lg border border-gray-300 bg-white;

    &-disabled {
      @apply border-gray-100 bg-gray-100;

      &:hover {
        @apply border-gray-100;
      }

      &:focus-within {
        @apply border-gray-100 ring-0;
      }
    }

    &:hover {
      @apply border-gray-400;
      @apply transition duration-100 ease-in-out;
    }

    &:focus-within {
      @apply border-gray-500 ring-4 ring-gray-600 ring-opacity-15;
    }

    &:deep() {
      .multiselect {
        @apply flex flex-row-reverse justify-between;
        @apply static min-h-full w-full overflow-hidden text-left;
        @apply pt-6;
      }

      .multiselect__tags {
        @apply rounded-none border-0 bg-transparent;
        @apply mb-px pb-3 pl-4 pr-4 pt-1;
        @apply min-h-full w-full;
      }

      .multiselect__input {
        @apply text-base font-normal text-gray-900;
        @apply !relative block !w-full align-top;
        @apply mb-0 !pb-0 !pl-0 !pr-10 !pt-px;
        @apply rounded-none border-none bg-white;

        &:focus {
          @apply shadow-none outline-none ring-0;
        }

        &::placeholder {
          @apply font-normal text-gray-400;
        }
      }

      .multiselect__placeholder {
        @apply hidden;
      }

      .multiselect--disabled {
        @apply rounded-lg bg-transparent;
        @apply opacity-100;
        @apply pointer-events-none;
      }

      .multiselect__content-wrapper {
        @apply my-2;
        @apply rounded-lg border border-gray-300 shadow;
        @apply absolute right-0 top-full;
        @apply z-50 block max-h-60 w-full overflow-auto;
        @apply bg-white;
        -webkit-overflow-scrolling: touch;
      }

      .multiselect__content {
        @apply inline-block list-none align-top;
        @apply m-0 min-w-full p-0;
      }

      .multiselect--above {
        .multiselect__content-wrapper {
          @apply bottom-full top-auto;
        }
      }

      .multiselect__element {
        @apply block;
      }

      .multiselect__option {
        @apply text-base font-normal normal-case text-gray-900 no-underline;
        @apply relative block cursor-pointer align-middle;
        @apply px-3 py-2;

        &:hover {
          @apply bg-gray-100;
        }

        &:active {
          @apply bg-gray-200 font-medium;
        }

        &:disabled {
          @apply text-gray-500;
        }
      }

      .multiselect__option--disabled {
        @apply !pointer-events-none !text-gray-300;
      }

      .multiselect__option--selected {
        @apply bg-gray-200 font-medium;

        &:hover {
          @apply bg-gray-200 font-medium;
        }
      }

      .multiselect__option--highlight {
        @apply bg-gray-100;
      }

      .multiselect--disabled {
        .multiselect__input {
          @apply !bg-gray-100;
        }

        .multiselect__single {
          @apply font-normal text-gray-900;
          @apply mb-px p-0;
          @apply relative inline-block w-full align-top;
          @apply border-none bg-gray-100;
        }
      }
    }
  }

  &-angle {
    @apply -mt-1.5 mr-4;
    @apply cursor-pointer;
  }

  &--active .icon {
    @apply rotate-180 transform transition-all duration-300;
  }

  &-clear {
    @apply absolute bottom-3.5 right-0 pr-4;
    @apply z-10 cursor-pointer;

    &-text {
      @apply transition-all duration-100 ease-in-out;
      @apply text-sm font-normal text-gray-400;

      &:hover {
        @apply text-gray-500;
      }
    }

    &:active {
      @apply text-gray-700;
    }
  }

  .tags {
    &-wrapper {
      @apply flex items-center justify-between;
      @apply border border-l-0 border-r-0 border-t-0 border-gray-100;
      @apply py-3;

      &:last-child {
        @apply mb-2.5;
      }
    }

    &-title {
      @apply text-base font-normal text-gray-900;
      @apply overflow-hidden text-ellipsis whitespace-nowrap;
    }
  }
}

.selected-count {
  @apply mr-2 inline-flex items-center;
  @apply text-base font-normal text-gray-900;
}

.default-tags:deep() {
  .multiselect__tags {
    @apply flex items-center pr-16;
  }

  .multiselect__input {
    @apply !w-auto min-w-[5rem] flex-1 !py-0.5;
  }
}

.default-tags.has-value:not(.is-open):deep(.multiselect__input) {
  @apply !hidden;
}

.icon {
  @apply transition-all duration-300;
}

.label {
  @apply absolute left-4 top-2.5 block;
  @apply text-sm font-normal text-gray-500;
  @apply z-10;

  &-color {
    @apply text-sm font-normal text-gray-500;
    z-index: 9999;
  }
}

.description {
  @apply text-xs font-normal text-gray-500/[85];
  @apply pl-4 pt-2;
}

.error {
  @apply border-red-300;

  &:hover {
    @apply border-red-400;
  }

  &:focus-within {
    @apply border-red-500 ring-4 ring-red-100;
  }

  &-label {
    @apply text-red-500;
  }

  &-message {
    @apply text-xs font-normal text-red-500;
    @apply mt-2 pl-4;
  }
}

.after-caret {
  @apply pr-4;
}

.group-select:deep(.multiselect__option--group) {
  @apply !bg-transparent;
  @apply !text-xs !font-medium uppercase !text-gray-500/[85];
}

.group-label {
  @apply text-xs font-medium uppercase text-gray-500/[85];
}

.multiselect-add {
  &-wrapper {
    @apply flex items-center justify-between;
    @apply cursor-pointer;
    @apply p-3;

    &:hover {
      @apply bg-gray-100;
    }

    &:active {
      @apply bg-gray-200 font-medium;
    }
  }

  &-title {
    @apply text-base font-medium text-gray-900;

    &-command {
      @apply text-gray-500;
    }
  }

  &-button {
    @apply absolute bottom-[0.5625rem] right-3;
    @apply rounded-full p-0;
  }

  &-icon {
    @apply bg-transparent;
    @apply p-0;

    &:deep(svg) {
      @apply !m-1;
    }

    &:deep(g [fill]) {
      @apply opacity-100;
    }
  }
}

.cross-out-option {
  @apply line-through;
}

.hierarchical-option {
  @apply flex items-center gap-2;
}

.hierarchical-option-dot {
  @apply shrink-0 rounded-full bg-gray-400;
}
</style>
